
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, DollarSign, AlertTriangle, Target, BarChart3 } from 'lucide-react';
import { calculateKeyValuationMetrics } from '@/lib/financial/valuation-metrics';

export function KeyValuationMetrics() {
  const [metrics, setMetrics] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Mock data for demonstration - in a real app this would come from props or context
    const mockResourceData = {
      tonnage: 1000000,
      grade: 2.5,
      mineralType: 'gold',
      classification: 'indicated' as const,
      marketValue: 50000000
    };

    const mockMarketData = {
      goldPrice: 1800,
      silverPrice: 24,
      copperPrice: 8500,
      commodityTrends: {
        gold: 'bullish',
        silver: 'neutral', 
        copper: 'bearish'
      }
    };

    const mockCostStructure = {
      miningCosts: 25,
      processingCosts: 15,
      administrativeCosts: 8,
      transportationCosts: 5
    };

    const mockRiskFactors = {
      geological: 0.2,
      political: 0.1,
      environmental: 0.15,
      technical: 0.1,
      market: 0.25
    };

    const mockFinancialParameters = {
      discountRate: 0.1,
      taxRate: 0.28,
      royaltyRate: 0.05
    };

    try {
      // Call the function with all required parameters
      const calculatedMetrics = calculateKeyValuationMetrics(
        mockResourceData,
        mockMarketData,
        mockCostStructure,
        mockRiskFactors,
        mockFinancialParameters
      );
      
      setMetrics(calculatedMetrics);
    } catch (error) {
      console.error('Error calculating metrics:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <div className="animate-pulse">Loading valuation metrics...</div>
        </CardContent>
      </Card>
    );
  }

  if (!metrics) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <div className="text-gray-500">Unable to calculate valuation metrics</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Key Metrics Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            Key Valuation Metrics
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                ${(metrics.netPresentValue / 1000000).toFixed(1)}M
              </div>
              <div className="text-sm text-gray-600">Net Present Value</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {(metrics.internalRateOfReturn * 100).toFixed(1)}%
              </div>
              <div className="text-sm text-gray-600">Internal Rate of Return</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">
                {metrics.paybackPeriod.toFixed(1)} yrs
              </div>
              <div className="text-sm text-gray-600">Payback Period</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600">
                {metrics.profitabilityIndex.toFixed(2)}
              </div>
              <div className="text-sm text-gray-600">Profitability Index</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Risk Assessment */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            Risk Assessment
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between items-center mb-2">
                <span>Overall Risk Score</span>
                <Badge variant={metrics.riskScore > 70 ? "destructive" : metrics.riskScore > 50 ? "secondary" : "default"}>
                  {metrics.riskScore.toFixed(0)}/100
                </Badge>
              </div>
              <Progress value={metrics.riskScore} className="h-2" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Market Risk:</span>
                <span className="ml-2 font-medium">{(metrics.marketRisk * 100).toFixed(0)}%</span>
              </div>
              <div>
                <span className="text-gray-600">Technical Risk:</span>
                <span className="ml-2 font-medium">{(metrics.technicalRisk * 100).toFixed(0)}%</span>
              </div>
              <div>
                <span className="text-gray-600">Political Risk:</span>
                <span className="ml-2 font-medium">{(metrics.politicalRisk * 100).toFixed(0)}%</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sensitivity Analysis */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Sensitivity Analysis
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {metrics.sensitivityAnalysis?.map((item: any, index: number) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <div className="font-medium capitalize">{item.variable}</div>
                  <div className="text-sm text-gray-600">±10% change impact</div>
                </div>
                <div className="text-right">
                  <div className="font-bold">
                    {item.impact > 0 ? '+' : ''}{(item.impact * 100).toFixed(1)}%
                  </div>
                  <div className="text-sm text-gray-600">NPV Impact</div>
                </div>
              </div>
            )) || (
              <div className="text-center text-gray-500 py-4">
                No sensitivity data available
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
